import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Flatpickr from 'react-flatpickr';
import useVenueAgeRestrictionOptions from '../../../shared/hooks/useVenueAgeRestrictionOptions';
import withSavedOption from '../../../shared/utils/withSavedOption';
import { offsetDateForBrowser } from '../../../../shared/timeZoneLogic';

const SimpleEventDetailsModal = ({ isOpen, offer, team, isSaving, onClose, onSave }) => {
  const [eventName, setEventName] = useState('');
  const [venueId, setVenueId] = useState('');
  const [startTime, setStartTime] = useState(''); // browser-local Date on open, Flatpickr Date[] after a pick
  const [ageRestriction, setAgeRestriction] = useState('');

  // team.venue_ownerships carries { id, venueable_id, name }; the offer's
  // venue_id IS the venueable_id, so that's the option value.
  const venueOwnerships = team?.venue_ownerships || [];
  // Age options follow the *selected* venue's ownership so switching venue in
  // the modal reloads the right list; falls back to the offer's own ownership.
  const selectedOwnershipId = venueOwnerships.find(
    (vo) => String(vo.venueable_id) === venueId,
  )?.id || offer?.venue_ownership_id;
  const { options: ageOptions } = useVenueAgeRestrictionOptions(team?.id, selectedOwnershipId);

  // Reseed from the offer whenever the modal (re)opens.
  useEffect(() => {
    if (!isOpen) return;
    setEventName(offer?.event_name || '');
    setVenueId(offer?.venue_id != null ? String(offer.venue_id) : '');
    // Relabel the server value into the browser's wall clock for display, using
    // the venue's timezone (matching V3CreateConfirmModal); the save path
    // reverses it with offsetDateForServer and the same tz.
    setStartTime(offer?.start_time ? offsetDateForBrowser(offer.start_time, offer.venue_time_zone || null) : '');
    setAgeRestriction(offer?.age_restriction || '');
  }, [isOpen, offer?.id]);

  if (!isOpen) return null;

  const ageChoices = withSavedOption(ageOptions, ageRestriction);

  return (
    <div
      className="modal d-block simple-event-modal"
      tabIndex="-1"
      style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
      data-testid="simple-event-modal"
    >
      <div className="modal-dialog">
        <div className="modal-content simple-event-modal__content">
          <div className="modal-header">
            <h5 className="modal-title">Edit event details</h5>
            <button type="button" className="close" onClick={onClose} disabled={isSaving} aria-label="Close">
              <span aria-hidden="true">&times;</span>
            </button>
          </div>

          <div className="modal-body">
            <div className="form-group">
              <label htmlFor="simple-event-name">Event name</label>
              <input
                id="simple-event-name"
                type="text"
                className="setlist-input"
                value={eventName}
                onChange={(e) => setEventName(e.target.value)}
                placeholder="Event name"
                data-testid="simple-event-name-input"
              />
            </div>

            <div className="form-group">
              <label htmlFor="simple-event-venue">Venue</label>
              <select
                id="simple-event-venue"
                className="setlist-input"
                value={venueId}
                onChange={(e) => {
                  setVenueId(e.target.value);
                  // Age options are venue-specific, so a stale value from the
                  // previous venue shouldn't ride along to the new one.
                  setAgeRestriction('');
                }}
                data-testid="simple-event-venue-select"
              >
                <option value="">Select venue…</option>
                {venueOwnerships.map((vo) => (
                  <option key={vo.id} value={vo.venueable_id}>{vo.name}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="simple-event-datetime">
                Date &amp; time <span className="simple-event-modal__hint">(leave blank for TBD)</span>
              </label>
              <Flatpickr
                id="simple-event-datetime"
                className="setlist-input"
                options={{ enableTime: true, dateFormat: 'F j, Y - h:i K' }}
                placeholder="Select date and time"
                value={startTime || ''}
                onChange={(dates) => setStartTime(dates)}
                data-testid="simple-event-datetime-input"
              />
            </div>

            <div className="form-group mb-0">
              <label htmlFor="simple-event-age">Age limit</label>
              <select
                id="simple-event-age"
                className="setlist-input"
                value={ageRestriction}
                onChange={(e) => setAgeRestriction(e.target.value)}
                data-testid="simple-event-age-select"
              >
                <option value="">No age limit</option>
                {ageChoices.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="modal-footer">
            <button type="button" className="btn btn-setlist-secondary btn-sm" onClick={onClose} disabled={isSaving}>
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary btn-sm ml-2"
              onClick={() => onSave({ eventName: eventName.trim(), venueId, startTime, ageRestriction })}
              disabled={isSaving || !eventName.trim()}
              data-testid="simple-event-save"
            >
              {isSaving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

SimpleEventDetailsModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  offer: PropTypes.object,
  team: PropTypes.shape({ id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]) }),
  isSaving: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
};

SimpleEventDetailsModal.defaultProps = {
  offer: null,
  team: null,
  isSaving: false,
};

export default SimpleEventDetailsModal;
